/*
 * LandingPage.tsx - Sangati App Landing Page
 *
 * This page serves as the entry point for users.
 *
 * Features:
 * - Detects and sets the user's language using the Languages API and i18n.
 * - Plays a welcome audio tune with a visual indicator.
 * - Navigates users to the appropriate next step based on their onboarding status.
 * - Responsive and accessible design.
 *
 * Usage:
 * This component is rendered at the root route ('/').
 */

import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Handle } from "../resources/types/authTypes";
import SangatiCard from "../components/SangatiCard";
import BeginButton from "../components/BeginButton";
import { safeLocalStorage, getSafeLanguage } from "../utils/webviewPolyfills";

const LandingPage: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qrHandle = searchParams.get("handle");

  // Load the handle with memo so that it only runs once on mount
  // This avoids unnecessary re-renders and performance issues
  const handle = useMemo(() => {
    try {
      // Add defensive checks for WebView environment
      const rawValue = safeLocalStorage.getItem("handle");
      if (!rawValue) return null;

      return JSON.parse(rawValue) as Handle;
    } catch (error) {
      console.warn("Error accessing localStorage or parsing handle:", error);
      safeLocalStorage.removeItem("handle");
      return null;
    }
  }, []); // Empty dependency array ensures this runs once on mount

  // On mount: set language and fetch available languages
  useEffect(() => {
    if (qrHandle && handle) {
      navigate("/sangati", {
        state: { matcheeqrHandle: qrHandle },
        replace: true,
      });
      return; // Exit early if qrHandle is present
    }

    // Set language from handle or browser
    const userLangCode = handle?.user?.language_code;
    const browserLangCode = getSafeLanguage();
    const targetLanguage = userLangCode || browserLangCode;

    if (i18n.language !== targetLanguage) {
      i18n.changeLanguage(targetLanguage);
    }
  }, [handle, i18n, navigate, qrHandle]);

  return (
    <div>
      <div className="min-h-svh flex gap-2 relative pb-12 bg-bg-grey">
        <div className="flex-1 bg-white85 relative overflow-hidden rounded-br-2xl">
          {/* for border */}
          <div className="absolute inset-0 border-r-2 border-[#00000033] rounded-br-2xl"></div>
          <img
            src="/images/crosspalms_left.png"
            alt="art"
            className="absolute right-0 bottom-0 h-[70%] w-auto opacity-80 object-cover object-right"
          />
        </div>
        <div className="flex-1 bg-black85 relative overflow-hidden rounded-bl-2xl">
          <img
            src="/images/crosspalms_right.png"
            alt="art"
            className="absolute left-0 bottom-0 h-[70%] w-auto opacity-80 object-cover object-left"
          />
          {/* for border */}
          <div className="absolute inset-0 border-l-2 border-[#00000033] rounded-bl-2xl"></div>
        </div>

        {/* sangati card */}
        <div className="absolute top-[10%] left-1/2 -translate-x-1/2">
          <SangatiCard />
        </div>

        {/* begin button */}
        <div className="absolute bottom-[25%] left-1/2 -translate-x-1/2">
          <BeginButton handle={handle} />
        </div>

        {/* Company text at the bottom */}
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 mb-4">
          <p className="text-center text-sm text-gray-600 whitespace-nowrap">
            © 2025 Kȧlpȧnecha Karkhana Private Limited
          </p>
        </div>
      </div>
      <div className="bg-bg-grey text-12 text-[#3B3C36] w-full">
        <div className="max-w-[786px] p-2 ps-5 flex items-center mx-auto">
          <div className="flex flex-col flex-1">
            <div className="mt-1">
              <Link to={"/contact"}>{t("contactUs")}</Link>
            </div>
            <div className="mt-1">
              <Link to={"/terms-and-condition"}>Terms and Conditions</Link>
            </div>
            <div className="mt-1">
              <Link to={"/privacy"}>Privacy Policy</Link>
            </div>
            <div className="mt-1">
              <Link to={"/refund-and-cancellation"}>
                Refund and Cancellations
              </Link>
            </div>
          </div>
          <div className="flex items-center justify-center ml-4">
            <img
              src="/images/karkhana_logo.png"
              alt="Karkhana Logo"
              className="h-16 w-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
